import React from 'react'

export const ScamExplained: React.FC = () => {
  return (
    <section id="scam-exposed" className="bg-white py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4">
            The <span className="text-slate-700">Timeshare Attorney Scam</span> Exposed
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Here's what the timeshare attorney industry doesn't want you to know about their 
            overpriced "services" and how you can get the same results for 90% less.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          {/* Left: The Problem */}
          <div className="bg-slate-800 border border-slate-600 rounded-lg p-8">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-slate-700 text-slate-300 rounded-full flex items-center justify-center mr-4">
                <svg className="w-6 h-6 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-white">What They Don't Tell You</h3>
            </div>
            
            <div className="space-y-6">
              <div>
                <h4 className="font-semibold text-slate-200 mb-2">🎯 Same Basic Strategies</h4>
                <p className="text-slate-300 text-sm">
                  Most attorneys use 3-4 standard legal templates and consumer protection laws. 
                  That's it. You're paying $10,000+ for form letters.
                </p>
              </div>
              
              <div>
                <h4 className="font-semibold text-slate-200 mb-2">💰 No Success Guarantee</h4>
                <p className="text-slate-300 text-sm">
                  They take your money upfront with no promise of results. If they fail, 
                  you still owe the full fee. Zero accountability.
                </p>
              </div>
              
              <div>
                <h4 className="font-semibold text-slate-200 mb-2">⏰ Deliberately Slow Process</h4>
                <p className="text-slate-300 text-sm">
                  They drag out simple processes to justify their high fees. What takes 
                  30 days becomes 12+ months of "legal work."
                </p>
              </div>
              
              <div>
                <h4 className="font-semibold text-slate-200 mb-2">🔒 Keep You in the Dark</h4>
                <p className="text-slate-300 text-sm">
                  Limited communication keeps you dependent. They don't want you to know 
                  how simple the actual process is.
                </p>
              </div>
            </div>
          </div>

          {/* Right: The Solution */}
          <div className="bg-blue-50 border border-blue-300 rounded-lg p-8">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center mr-4">
                <svg className="w-6 h-6 text-blue-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-blue-800">The Professional Reality</h3>
            </div>
            
            <div className="space-y-6">
              <div>
                <h4 className="font-semibold text-blue-700 mb-2">📋 Public Legal Strategies</h4>
                <p className="text-blue-600 text-sm">
                  The laws and templates they use are public information. Our guide 
                  includes everything they use, plus company-specific strategies.
                </p>
              </div>
              
              <div>
                <h4 className="font-semibold text-blue-700 mb-2">🚀 You Can Do It Faster</h4>
                <p className="text-blue-600 text-sm">
                  Without attorney delays, most people complete the exit process in 
                  3-6 months. No waiting, no red tape.
                </p>
              </div>
              
              <div>
                <h4 className="font-semibold text-blue-700 mb-2">🎯 Better Success Rate</h4>
                <p className="text-blue-600 text-sm">
                  Our customers often get better results because they're fighting for themselves, 
                  not just another billable hour.
                </p>
              </div>
              
              <div>
                <h4 className="font-semibold text-blue-700 mb-2">💡 Complete Transparency</h4>
                <p className="text-blue-600 text-sm">
                  You'll understand every step, every law, every strategy. Knowledge 
                  is power, and we give you both.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Case Study */}
        <div className="bg-slate-50 border border-slate-300 rounded-lg p-8">
          <h3 className="text-2xl font-bold text-slate-800 mb-6 text-center">
            Professional Case Study: Sarah vs. The Attorney Scam
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-slate-800 border border-slate-600 rounded-lg p-6">
              <h4 className="font-semibold text-white mb-3">Sarah's Attorney Experience</h4>
              <ul className="text-slate-300 space-y-2 text-sm">
                <li>• Paid $12,000 upfront to "top-rated" attorney</li>
                <li>• 14 months of waiting with minimal updates</li>
                <li>• Attorney used basic template letters</li>
                <li>• Case ultimately failed - no refund</li>
                <li>• <strong className="text-white">Total loss: $12,000 + ongoing fees</strong></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-blue-700 mb-3">Sarah's DIY Success (Round 2)</h4>
              <ul className="text-blue-600 space-y-2 text-sm">
                <li>• Purchased our guide for $997</li>
                <li>• Successfully exited in 4 months</li>
                <li>• Used the same laws her attorney ignored</li>
                <li>• Saved $11,000+ vs. second attorney quote</li>
                <li>• <strong>Total investment: $997 - Success guaranteed</strong></li>
              </ul>
            </div>
          </div>
          
          <div className="mt-6 text-center">
            <p className="text-slate-700 font-medium">
              "I wish I had found this guide first. Could have saved myself a year of stress and $12,000." - Sarah M.
            </p>
          </div>
        </div>
      </div>
    </section>
  )
}